import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  CloudArrowUpIcon, 
  DocumentTextIcon, 
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Get max file size from environment variable (default 5GB if not set)
const MAX_FILE_SIZE = parseInt(process.env.REACT_APP_MAX_FILE_SIZE || '5368709120', 10);
const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

export default function FileUpload({ onFileUpload, disabled = false }) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError(null);
    
    if (rejectedFiles && rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        setError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        setError('Invalid file type. Only video and audio files are allowed.');
      } else {
        setError('File upload failed. Please try again.');
      }
      return;
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // Validate file type
      const validTypes = [
        // Video types
        'video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm', 'video/quicktime',
        // Audio types
        'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/flac', 'audio/aac', 'audio/ogg', 'audio/wma', 'audio/m4a'
      ];
      
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Only video and audio files are allowed.');
        return;
      }
      
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
        return;
      }
      
      // File is valid, call the callback
      onFileUpload(file);
    }
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'],
      'audio/*': ['.mp3', '.wav', '.flac', '.aac', '.ogg', '.wma', '.m4a']
    },
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    disabled: disabled
  });

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  return (
    <div className="w-full">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        onDragEnter={disabled ? undefined : handleDrag}
        onDragLeave={disabled ? undefined : handleDrag}
        onDragOver={disabled ? undefined : handleDrag}
        onDrop={disabled ? undefined : handleDrop}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200
          ${disabled
            ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
            : isDragActive || dragActive
            ? 'border-blue-500 bg-blue-50 cursor-pointer'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
          }
        `}
      >
        <input {...getInputProps()} />
        
        <div className="space-y-4">
          <div className="flex justify-center">
            <div className={`
              p-4 rounded-full transition-colors duration-200
              ${isDragActive || dragActive
                ? 'bg-blue-100 text-blue-600'
                : 'bg-gray-100 text-gray-600'
              }
            `}>
              <CloudArrowUpIcon className="h-12 w-12" />
            </div>
          </div>
          
          <div>
            <h3 className={`
              text-lg font-semibold transition-colors duration-200
              ${isDragActive || dragActive ? 'text-blue-700' : 'text-gray-700'}
            `}>
              {isDragActive || dragActive ? 'Drop your file here' : 'Drag & drop your file here'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              or click to browse
            </p>
          </div>
          
          <div className="text-xs text-gray-400 space-y-1">
            <p>Supported formats: MP4, AVI, MOV, MP3, WAV, FLAC</p>
            <p>Maximum file size: {MAX_FILE_SIZE_MB}MB</p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* File Type Icons */}
      <div className="mt-6 flex justify-center space-x-6">
        <div className="text-center">
          <div className="p-3 bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
            <DocumentTextIcon className="h-6 w-6 text-blue-600" />
          </div>
          <p className="text-xs text-gray-600">Video Files</p>
        </div>
        
        <div className="text-center">
          <div className="p-3 bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
            <DocumentTextIcon className="h-6 w-6 text-green-600" />
          </div>
          <p className="text-xs text-gray-600">Audio Files</p>
        </div>
      </div>
    </div>
  );
}
